/**
 * Core Types
 */

/**
 * A single problem from an API error response. `target` names the field the problem relates to and
 * `code` identifies the failure, so callers never have to match on the message text.
 */
export interface ApiErrorDetail {
  target?: string;
  message?: string;
  code?: string;
}

/**
 * Outcome of verifying a device's API key against its customer's tenancy. `result` is the serialised
 * enum ordinal; `warning` is only populated when the key could not be checked at all.
 */
export interface ApiKeyVerification {
  result: number;
  warning?: string;
}

/**
 * PagedResults interface
 */
export interface PagedResults<T> {
  pageNumber: number;
  pageSize: number;
  totalNumberOfPages: number;
  totalNumberOfRecords: number;
  nextPageUrl: string;
  previousPageUrl: string;
  firstPageUrl: string;
  lastPageUrl: string;
  results: T[];
}

/**
 * PagerOptions interface
 */
export interface PagerOptions {
  search?: { type: string; searchString: string }[];
  filter?: { type: string; filterString: string };
  page?: number;
  pageSize?: number;
  orderBy?: string;
  ascending?: boolean;
  usePaging?: boolean;
}

/**
 * FilterOption interface
 */
export interface FilterOption {
  id: string;
  name: string;
}

/**
 * Customer interface
 */
export interface Customer {
  id?: string;
  name: string;
  enabled: boolean;
  cloudInstanceUrl: string;
  subdomain: string;
  tenantId?: string;
  customerType?: Lookup,
  createdDate?: Date,
  subscriptionStartDate?: Date,
  subscriptionEndDate?: Date,
  subscriptionDetails?: SubscriptionDetails,
  poNumber?: string,
  contact?: string,
  integratorName?: string;
  integratorContact?: string;
  subscriptionDetailsId?: string,
  customerInstanceUrlId?: string,
  customerTypeId?: string,
  subdomainId?: string,
  subscriptionWarning?: SubscriptionWarning;
  assetCount?: number;
  combinedUsage?: CombinedUsage;
}

export type SubscriptionWarning = 'none' | 'high' | 'severe' | 'outOfContract';

export interface SubscriptionDetails {
  assetLimit?: number,
  storageLimit?: number,
  concurrentUsersLimit?: number,
  incidentCreationLimit?: number,
  liveVideoLimit?: number,
  redactionLimit?: number,
  /** Monthly XBA transcode allowance in minutes of produced video, summed across the clips each transcode makes. */
  xbaTranscodeLimit?: number,
  redactionEnabled?: boolean,
  cloudServicesEnabled?: boolean,
  streamVideoEnabled?: boolean,
  redactionTrialStartedAt?: Date,
  redactionTrialExpiresAt?: Date
}

export interface FixedCustomerManagementStatsRequest {
  tenantId: string;
  subscriptionStartDate: Date;
}

export interface CustomerManagementStats {
  tenantId: string,
  loggedInUsers?: number,
  totalUsers?: number,
  totalUserGroups?: number,
  incidentsCreated?: number,
  minutesStreamed?: number,
  secondsRedacted?: number,
  xbaTranscodeMinutes?: number,
  blobStorageUsed?: number
}

export interface CombinedUsage {
  blobStorageUsed: number;
  incidentsCreated: number;
  loggedInUsers: number;
  minutesStreamed: number;
  secondsRedacted: number;
  xbaTranscodeMinutes: number;
}

export interface FormattedUsage {
  assetUsage: string;
  storageUsed: string;
  incidentsCreated: string;
  loggedInUsers: string;
  liveVideoStreamed: string;
  redactedSeconds: string;
}

export interface Lookup {
  id?: string;
  name: string;
  displayName?: string;
}

export interface ContractTerm {
  id?: string;
  valueInMonths: number;
}

export interface HasId {
  id?: string;
}

export interface NotificationUser {
  enabled: boolean;
  allowanceNotifications: AllowanceNotification;
  subscriptionNotifications: SubscriptionNotification;
}

export interface DeviceManagementUser {
  celUserId: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface AllowanceNotification {
  ninetyPercent: boolean;
  hundredPercent: boolean;
}

export interface SubscriptionNotification {
  ninetyDays: boolean;
  thirtyDays: boolean;
  zeroDays: boolean;
}

/**
 * Device interface
 */
export interface Device {
  id?: string;
  serialNumber: string;
  name?: string;
  fleetId: string;
  enabled: boolean;
  customerId?: string;
  customer?: Customer;
  assetTypeId?: string;
  assetType?: Lookup;
  enableRecordingMapRequest?: boolean;
  enableCamerasSnapshotRequest?: boolean;
  apiKey?: string;
  apiKeyVerification?: ApiKeyVerification;
  assetGroupPath?: string;
  firmware?: string;
  hardware?: string;
  hwVer?: string;
  hardwareVersion?: string;
  slaveDevice?: string;
  slaveVersion?: string;
  deviceId?: string;
  online?: boolean;
  lastPinged?: Date;
  logsRequest?: LogsRequest;
  sim?: string;
}

/**
 * Device Firmware Upgrade Request interface
 */
export interface FirmwareUpgradeRequest {
  firmwareFilename?: string;
  deviceSerialNos?: string[];
  delayed?: number;
}

/**
 * Device Delete Many Request interface
 */
export interface DeviceDeleteManyRequest {
  deviceIds?: string[];
}

/**
 * Device Update Many Request interface
 */
export interface DeviceUpdateManyRequest {
  devices?: Device[];
  apiKeyUpdated?: boolean;
  customerUpdated?: boolean;
}

export interface LogsRequest {
  addedDate: Date;
  uploadedDate?: Date;
}

export interface FreeSearch {
  property: Lookup;
  searchTerm: string;
}

export interface TenancyConfiguration {
  incidentRetentionPeriodInDays: number;
}

export interface IncidentData {
    incidentsCreatedLastHourCategoryCounts: {
        categoryName: string | null;
        count: number;
    }[];
    incidentsOriginatorLastHourCounts: {
        originatorName: string | null;
        count: number;
    }[];
    incidentsCreatedLastHourCount: number;
    incidentSharesInLastHourCount: number;
    usersCreatedInLastHourCount: number;
    downloadAllInLastHourCount: number;
    downloadClipInLastHourCount: number;
    downloadClipMp3InLastHourCount: number;
    redactionRequestsLastHourCount: number;
    redactionSecondsLastHourCount: number;
    audioRedactionRequestsInTimeFrame: number | null;
    storageIncreaseInTimeFrame: number | null;
    minutesStreamedInTimeFrame: number | null;
    totalIncidentsShared: number;
    totalIncidents: number;
    activeIncidents: number;
    deletedIncidents: number;
    totalIncidentCategoriesCount: number;
    totalStorageUsage: number;
}

export interface UsageApiResponse {
    [timestamp: string]: IncidentData;
}

export type ForecastedUsageApiResponse = {
  [timestamp: string]: {actualUsage: IncidentData, forecastedUsage: IncidentData};
}

export type TenantUsageData = {
  usage: ForecastedUsageApiResponse;
  endOfFirstRetention: Date | null;
  customer: Customer;
}

export interface ExportData {
  deletedCount: number;
  successfulCount: number;
  failedCount: number;
  partiallyRetrievedCount: number;
  partiallyCompleteCount: number;
  cancelledCount: number;
}

export interface ExportRetryData {
  totalRetryAttempts: number;
  successfulRetries: number;
  failedRetries: number;
}

export type DataFlag = { icon: string, tooltip: string, color: string, showLeftOfLabel: boolean };

export type DataPoint = { name: string; actual?: number, predicted?: number };

export type GraphType = 'bar' | 'line';

export type GraphLabelType = 'normal' | 'hover';

export type Graph = { 
  description: string;
  type: GraphType; 
  color: string;
  data: DataPoint[]; 
  labelType: GraphLabelType;
};

export type GraphPair = { 
  leftGraph: Graph; 
  rightGraph: Graph; 
  startDate: Date;
};

export interface IPieChartItem {
    name: string;
    value: number;
    color: string;
}

/** Platform-wide usage totals for a single month (all tenants), from the Synx metrics endpoint. */
export interface PlatformMetricsMonthly {
    year: number;
    month: number;
    incidentsCreated: number;
    totalStorageBytes: number;
    storageAddedBytes: number;
    videoMinutesStreamed: number;
    redactionSeconds: number;
    clipDataAddedBytes: number;
    clipsAdded: number;
    uniqueLogins: number;
}

/** A single month's storage figures for one tenant (or the aggregated "Other" series). */
export interface StorageTenantMonthlyPoint {
    year: number;
    month: number;
    totalStorageBytes: number;
    /** Storage added during the month (bytes). Lets the "Storage Added" chart stack by tenant from the
     *  same series as total storage, so both share a tenant set, colours and legend. */
    storageAddedBytes: number;
}

/** One series for the stacked storage chart: a tenant's monthly totals, or the "Other" remainder. */
export interface StorageTenantSeries {
    tenantName: string;
    monthly: StorageTenantMonthlyPoint[];
    /** For the "Other" series only: the individual tenants folded into it, so the chart can break it
     *  down on hover. Absent on a normal, single-tenant series. */
    members?: StorageTenantSeries[];
}

/** Response from the Synx metrics endpoint: the monthly series plus range-level figures. */
export interface PlatformMetricsResult {
    months: PlatformMetricsMonthly[];
    /** Distinct users across the whole window (deduplicated), not a sum of the monthly figures. */
    uniqueUsersInRange: number;
    /** Top tenants by latest-month storage plus an "Other" series; each month sums to totalStorageBytes. */
    storageByTenant: StorageTenantSeries[];
}

/** Per-tenant usage totals over the selected range, for the KPI drill-down. */
export interface TenantMetricBreakdown {
    tenantId: string;
    tenantName: string;
    incidentsCreated: number;
    videoMinutesStreamed: number;
    redactionSeconds: number;
    clipDataAddedBytes: number;
    clipsAdded: number;
    uniqueUsers: number;
}

/** One month's figures for a single tenant, for the drill-down's per-tenant stacked charts. */
export interface TenantMonthlyUsagePoint {
    year: number;
    month: number;
    incidentsCreated: number;
    videoMinutesStreamed: number;
    redactionSeconds: number;
    clipDataAddedBytes: number;
    clipsAdded: number;
    uniqueLogins: number;
}

/** One tenant's monthly usage series (every drill-down metric) over the selected window. Sparse: only
 *  months the tenant had activity in appear - the dashboard zero-fills against its own month axis. */
export interface TenantMonthlyUsage {
    tenantName: string;
    monthly: TenantMonthlyUsagePoint[];
}
